// Centralized API configuration - purely environment-driven
export const API_BASE_URL = process.env.REACT_APP_API_URL;

// Validate that API URL is provided
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required but not provided');
}

// Admin organization configuration
export const ADMIN_ORGANIZATION_ID = process.env.REACT_APP_ADMIN_ORG_ID || '62dd1242-b0ba-4412-aea0-35479b4c5117';

// Debug logging for admin organization ID
console.log('🔧 Frontend Admin Org ID Configuration:');
console.log('  REACT_APP_ADMIN_ORG_ID:', process.env.REACT_APP_ADMIN_ORG_ID);
console.log('  Final ADMIN_ORGANIZATION_ID:', ADMIN_ORGANIZATION_ID);

// Validate admin organization ID format
if (ADMIN_ORGANIZATION_ID && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ADMIN_ORGANIZATION_ID)) {
  console.warn('⚠️ ADMIN_ORGANIZATION_ID does not appear to be a valid UUID format:', ADMIN_ORGANIZATION_ID);
}

// API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    ME: '/auth/me',
  },
  ORGANIZATIONS: {
    LIST: '/organizations',
    SEARCH: '/organizations/search',
    // Legacy broad endpoints (deprecated)
    REPORT: (orgId) => `/organizations/${orgId}/report`,
    PERFORMANCE: (orgId) => `/organizations/${orgId}/performance`,
    SENSOR_SUMMARY: (orgId) => `/organizations/${orgId}/sensor-summary`,
    // New granular report endpoints
    REPORT_DAILY_CONSUMPTION: (orgId) => `/organizations/${orgId}/report/daily-consumption`,
    // New granular performance endpoints
    PERFORMANCE_CONNECTIVITY: (orgId) => `/organizations/${orgId}/performance/connectivity`,
    PERFORMANCE_BATTERY_VOLTAGE: (orgId) => `/organizations/${orgId}/performance/battery-voltage`,
    PERFORMANCE_CONFIGURATION: (orgId) => `/organizations/${orgId}/performance/configuration`,
    PERFORMANCE_SUCCESS_RATE: (orgId) => `/organizations/${orgId}/performance/success-rate`, // Admin only
    PERFORMANCE_RECOMMENDATIONS: (orgId) => `/organizations/${orgId}/performance/recommendations`, // Admin only
    // Admin utility endpoints
    DEBUG_COUNT_STATUS: (orgId) => `/organizations/${orgId}/debug-count-status`, // Admin only
    REQUEST_VISUAL_CONFIRMATION: (orgId) => `/organizations/${orgId}/request-visual-confirmation`, // Admin only
    CONFIGURATION: (orgId) => `/organizations/${orgId}/configuration`, // Shadow configuration (reported + desired)
    // Logs endpoints (Admin only)
    LOGS_AVAILABILITY: (orgId) => `/organizations/${orgId}/logs/availability`, // Admin only
    LOGS_DEVICE: (orgId, deviceId) => `/organizations/${orgId}/logs/device/${deviceId}`, // Admin only
    LOGS_DEVICE_EXPORT: (orgId, deviceId) => `/organizations/${orgId}/logs/device/${deviceId}/export`, // Admin only
    // Card endpoints
    CARDS_TOTAL_DEVICES: (orgId) => `/organizations/${orgId}/cards/total-devices`,
    CARDS_DEVICES_WITH_DATA: (orgId) => `/organizations/${orgId}/cards/devices-with-data`,
    CARDS_TOTAL_USAGE: (orgId) => `/organizations/${orgId}/cards/total-usage`,
    CARDS_CONNECTIVITY_SUMMARY: (orgId) => `/organizations/${orgId}/cards/connectivity-summary`,
    CARDS_BATTERY_SUMMARY: (orgId) => `/organizations/${orgId}/cards/battery-summary`,
    CARDS_AVERAGE_USAGE: (orgId) => `/organizations/${orgId}/cards/average-usage`, // Admin only
    CARDS_SUCCESS_RATE_SUMMARY: (orgId) => `/organizations/${orgId}/cards/success-rate-summary`, // Admin only
    CARDS_RSSI_SUMMARY: (orgId) => `/organizations/${orgId}/cards/rssi-summary`, // Admin only
    // Organization role management endpoints (Admin only)
    ROLES: (orgId) => `/organizations/${orgId}/roles`,
    ROLES_BY_TYPE: (orgId, roleType) => `/organizations/${orgId}/roles/${roleType}`,
    ROLES_CHECK: (orgId, userId, roleType) => `/organizations/${orgId}/roles/${userId}/${roleType}/check`,
    ROLES_STATS: (orgId) => `/organizations/${orgId}/roles/stats`,
  },
  DEVICES: {
    LIST: '/devices',
    DETAILS: (deviceId) => `/devices/${deviceId}`,
    USERS: (deviceId) => `/devices/${deviceId}/users`,
    PICTURES: (deviceId) => `/devices/${deviceId}/pictures`,
    UPDATE: (deviceId) => `/devices/${deviceId}`,
    // Performance endpoints
    PERFORMANCE_CONNECTIVITY: (deviceId) => `/devices/${deviceId}/performance/connectivity`,
    PERFORMANCE_CONNECTION_COUNT: (deviceId) => `/devices/${deviceId}/performance/connection-count`,
    PERFORMANCE_PAYLOAD_COUNT: (deviceId) => `/devices/${deviceId}/performance/payload-count`,
    PERFORMANCE_CONNECTIVITY_PATTERN: (deviceId) => `/devices/${deviceId}/performance/connectivity-pattern`,
    PERFORMANCE_BATTERY_DAILY: (deviceId) => `/devices/${deviceId}/performance/battery-daily`,
    PERFORMANCE_RSSI_DAILY: (deviceId) => `/devices/${deviceId}/performance/rssi-daily`,
    PERFORMANCE_TEMPERATURE_DAILY: (deviceId) => `/devices/${deviceId}/performance/temperature-daily`,
    PERFORMANCE_BOOT_REASON: (deviceId) => `/devices/${deviceId}/performance/boot-reason`,
    // Report endpoints
    REPORT_DAILY_CONSUMPTION: (deviceId) => `/devices/${deviceId}/report/daily-consumption`,
  },
  CELLULAR: {
    OVERVIEW: (orgId) => `/cellular/overview/${orgId}`,
    SIMS: (orgId) => `/cellular/sims/${orgId}`,
    TRAFFIC_LOGS: (orgId) => `/cellular/traffic-logs/${orgId}`,
    SIM_TRAFFIC_LOGS: (simId) => `/cellular/sims/${simId}/traffic-logs`,
    SIM_SIGNALLING_LOGS: (simId) => `/cellular/sims/${simId}/signalling-logs`,
    NETWORK_WHITELISTS: '/cellular/network-whitelists',
    SIM_DETAILS: (simId) => `/cellular/sims/details/${simId}`,
    SIM_SEARCH: '/cellular/sims/search',
  },
  APPLICATIONS: {
    // User endpoints
    LIST: '/applications',
    CATEGORIES: '/applications/categories',
    DETAILS: (appId) => `/applications/${appId}`,
    SENSORS: (appId) => `/applications/${appId}/sensors`,
    
    // Management endpoints (admin only)
    MANAGEMENT: {
      LIST: '/applications/management/all',
      CREATE: '/applications/management',
      UPDATE: (appId) => `/applications/management/${appId}`,
      DELETE: (appId) => `/applications/management/${appId}`,
      ORGANIZATIONS: (appId) => `/applications/management/${appId}/organizations`,
    },
    
    // Organization access endpoints (admin only)
    ORGANIZATIONS: {
      LIST: (orgId) => `/applications/organizations/${orgId}/applications`,
      GRANT: (orgId, appId) => `/applications/organizations/${orgId}/applications/${appId}`,
      REVOKE: (orgId, appId) => `/applications/organizations/${orgId}/applications/${appId}`,
      UPDATE: (orgId, appId) => `/applications/organizations/${orgId}/applications/${appId}`,
      SET_DEFAULT: (orgId) => `/applications/organizations/${orgId}/applications/default`,
      GET_DEFAULT: (orgId) => `/applications/organizations/${orgId}/applications/default`,
    }
  },
  SENSORS: {
    APPLICATION: (sensorId) => `/sensors/${sensorId}/application`,
    EFFECTIVE_APPLICATION: (sensorId) => `/sensors/${sensorId}/effective-application`,
    ASSIGN_APPLICATION: (sensorId) => `/sensors/${sensorId}/application`,
    BULK_ASSIGN: '/sensors/bulk-assign',
    BULK_UNASSIGN: '/sensors/bulk-unassign',
  },
  TRIGGERS: {
    LIST: '/triggers',
    CREATE: '/triggers',
    UPDATE: (triggerId) => `/triggers/${triggerId}`,
    DELETE: (triggerId) => `/triggers/${triggerId}`,
    DETAILS: (triggerId) => `/triggers/${triggerId}`,
    EVENT_SOURCES: '/triggers/event-sources',
  },
  ACTIONS: {
    LIST: '/actions',
    CREATE: '/actions',
    UPDATE: (actionId) => `/actions/${actionId}`,
    DELETE: (actionId) => `/actions/${actionId}`,
    DETAILS: (actionId) => `/actions/${actionId}`,
    CATEGORIES: '/actions/categories',
  },
  TRIGGER_ACTIONS: {
    LIST: (applicationId) => `/applications/${applicationId}/trigger-actions`,
    CREATE: (applicationId) => `/applications/${applicationId}/trigger-actions`,
    UPDATE: (applicationId, associationId) => `/applications/${applicationId}/trigger-actions/${associationId}`,
    DELETE: (applicationId, associationId) => `/applications/${applicationId}/trigger-actions/${associationId}`,
  },
  AGENTIC: {
    CHAT: '/agentic/chat',
    RESPONSE: (requestId) => `/agentic/response/${requestId}`,
    THREADS: '/agentic/threads',
    THREAD_MESSAGES: (threadId) => `/agentic/threads/${threadId}/messages`,
    DEVICE_LOOKUP: '/agentic/devices/lookup',
    TELEMETRY_QUERY: '/agentic/telemetry/query',
    HEALTH: '/agentic/health',
  },
  TRIGGERS: {
    LIST: '/triggers',
    CREATE: '/triggers',
    UPDATE: (triggerId) => `/triggers/${triggerId}`,
    DELETE: (triggerId) => `/triggers/${triggerId}`,
    TOGGLE_STATUS: (triggerId) => `/triggers/${triggerId}/toggle-status`,
    EVENT_SOURCES: '/triggers/event-sources',
  },
  ACTIONS: {
    LIST: '/actions',
    CREATE: '/actions',
    UPDATE: (actionId) => `/actions/${actionId}`,
    DELETE: (actionId) => `/actions/${actionId}`,
    TOGGLE_STATUS: (actionId) => `/actions/${actionId}/toggle-status`,
    CATEGORIES: '/actions/categories',
  },
  TRIGGER_ACTION_ASSOCIATIONS: {
    LIST: (applicationId) => `/applications/${applicationId}/trigger-action-associations`,
    CREATE: (applicationId) => `/applications/${applicationId}/trigger-action-associations`,
    UPDATE: (applicationId, associationId) => `/applications/${applicationId}/trigger-action-associations/${associationId}`,
    DELETE: (applicationId, associationId) => `/applications/${applicationId}/trigger-action-associations/${associationId}`,
    REORDER: (applicationId) => `/applications/${applicationId}/trigger-action-associations/reorder`,
  },
  USERS: {
    LIST: '/users',
    DETAILS: (userId) => `/users/${userId}`,
    // User organization roles (Admin only)
    ORGANIZATION_ROLES: (userId) => `/users/${userId}/organization-roles`,
  },
  ORGANIZATION_ROLES: {
    // Global role management (Admin only)
    ALL_USERS_WITH_ROLE: (roleType) => `/organization-roles/${roleType}`,
  },
  STATS: '/stats',
}; 